import { Component, Output, EventEmitter, OnDestroy } from "@angular/core";
import { Store, select } from "@ngrx/store";

import { Observable, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { CopyHelpers } from "../../helpers/copy.helpers";
import { arrayRange } from "../../helpers/array.helper";
import {
  updateItemFromReturnOrderAction,
  addItemToReturnOrderAction,
  removeItemFromReturnOrderAction,
} from "../../../store/actions/order.actions";
import { TranslateService } from "@ngx-translate/core";
import { TranslationDataService } from "../../services/translation-data.service";
import { clientConfig } from "../../constant/app.constant";
import { SpinnerService } from "../../services/spinner.service";
import { ReturnOrderService } from "../../services/return-order/return-order.service";
import { ExternalIntegrationsService } from "../../services/externalIntegration.service";

@Component({
  selector: "app-order-item-details",
  templateUrl: "./order-item-details.component.html",
  styleUrls: ["./order-item-details.component.scss"],
})
export class OrderItemDetailsComponent implements OnDestroy {
  @Output() returnItemChangeEvent = new EventEmitter<any>();
  orderData: any;
  reasonCheck = false;
  status = false;
  quantity = false;
  returnOrder$: Observable<any>;
  arrayRangeFn = arrayRange;
  returnReasonsData: any;
  translationSubscription: Subscription;
  tenantName = clientConfig.TENANTNAME;
  selectedParcelCount: number = 1;
  parcelCounts: number[] = [];
  showParcelCountDropdown: boolean = false;
  useExternalIntegration: boolean = clientConfig.USE_EXTERNAL_INTEGRATION;
  extractedShopifyItems: any[] = [];

  constructor(
    private store: Store<AppState>,
    private translate: TranslateService,
    private translationService: TranslationDataService,
    private spinnerService: SpinnerService,
    private returnOrderService: ReturnOrderService,
    private externalIntegrationsService: ExternalIntegrationsService
  ) {
    this.returnOrder$ = this.store.pipe(select("returnOrder"));
    this.store.pipe(select("orderData")).subscribe((order) => {
      this.orderData = CopyHelpers.deepCopy(order);
    });
    this.store.pipe(select("returnOrder")).subscribe((returnOrder) => {
      if (returnOrder.items.length) {
        //copy data from return order and
        //remaing from order data to show on UI
        //find order
        if (this.orderData.orders.length) {
          const order = this.orderData.orders.find((o) => o.orderNumber === returnOrder.orderNumber);

          if (order) {
            order.orderLines = order.orderLines.map((item) => {
              const returnItem = returnOrder.items.find((i) => i.internalIdentifier === item.internalIdentifier);
              if (returnItem) return returnItem;

              delete item.invalidProps;
              return item;
            });
          }
        }

        this.orderData = CopyHelpers.deepCopy(this.orderData);
      }
    });

    if (this.tenantName.trim().toLowerCase() === "pandora") {
      this.translationService.loadReasons(translate.defaultLang).subscribe((reasonCodes) => {
        //Initial fallback occurred due to unrecognized 2-character browser language setting.
        this.returnReasonsData = reasonCodes;
      });

      this.translate.onLangChange.subscribe((event) => {
        this.translationService.loadReasons(translate.currentLang).subscribe(
          (res) => {
            this.returnReasonsData = res;
          },
          (error) => {
            console.error("Error loading reasons:", error);
            const errorMessage = error?.error?.message || error?.message || JSON.stringify(error);
            window.alert(errorMessage);
          }
        );
      });
    } else {
      //For getting return reason code from translation service
      this.translationService.fetchTranslationData(translate.currentLang === "en" ? "en-GB" : translate.currentLang); //To load the reason codes at first time
      this.translate.onLangChange.subscribe((event) => {
        this.translationService.fetchTranslationData(event.lang);
      });

      this.translationSubscription = this.translationService.translationDataEmitter.subscribe((res) => {
        this.returnReasonsData = res;
      });
    }
  }

  ngOnInit() {
    this.status = false;

    let parcelCount = this.returnOrderService.parcelCount;
    this.selectedParcelCount = this.returnOrderService.selectedParcelCount;
    this.parcelCounts = Array.from({ length: parcelCount }, (_, i) => i + 1);
    this.showParcelCountDropdown = this.tenantName.toLowerCase().trim() === "dhl";

    this.validateSelectedReturnItem();

    if (this.useExternalIntegration) {
      this.getShopifyData();
    }
  }

  updateSelectedParcelCount(count: number) {
    this.selectedParcelCount = count;
    this.returnOrderService.selectedParcelCount = this.selectedParcelCount;
  }

  selectReasonForReturn(event, item) {
    const reasonForReturn = event.target.value;
    item.reasonForReturn = reasonForReturn;
    this.reasonCheck = true;

    this.store.dispatch(
      updateItemFromReturnOrderAction({
        payload: {
          item: { reasonForReturn: reasonForReturn },
          itemNumber: item.itemNumber,
        },
      })
    );
    if (item.returnableQuantity === 1) {
      this.store.dispatch(
        updateItemFromReturnOrderAction({
          payload: {
            item: { returnQuantity: 1 },
            itemNumber: item.itemNumber,
          },
        })
      );
    }
    this.validateSelectedReturnItem();
  }

  selectQtyForReturn(event, item) {
    item.returnQuantity = event.target.value;

    this.store.dispatch(
      updateItemFromReturnOrderAction({
        payload: {
          item: { returnQuantity: event.target.value },
          itemNumber: item.itemNumber,
        },
      })
    );
    this.validateSelectedReturnItem();
  }
  selectReturnItems(item) {
    item.active = !item.active;
    if (item.active) {
      this.store.dispatch(
        addItemToReturnOrderAction({
          payload: { item: CopyHelpers.deepCopy(item) },
        })
      );
    } else {
      //item.reasonForReturn = null;
      //item.returnQuantity = null;
      this.store.dispatch(
        removeItemFromReturnOrderAction({
          payload: { item: CopyHelpers.deepCopy(item) },
        })
      );
    }
    this.validateSelectedReturnItem();
  }

  validateReturnOrderData() {
    let returnOrder: any;
    this.store
      .pipe(select("returnOrder"))
      .pipe(take(1))
      .subscribe((ro) => {
        returnOrder = ro;
      });
    if ((returnOrder.items && returnOrder.items.length) || true) {
      if (this.orderData.orders.length) {
        const order = this.orderData.orders.find((o) => o.orderNumber === returnOrder.orderNumber);
        if (order) {
          order.orderLines.forEach((orderLine) => {
            const item = returnOrder.items.find((i) => i.internalIdentifier === orderLine.internalIdentifier);
            const invalidProps = [];
            if (item) {
              if (item.returnableQuantity === 1) {
                orderLine.returnQuantity = 1;
              }
              if (!orderLine.reasonForReturn || orderLine.reasonForReturn === "") {
                invalidProps.push("reasonForReturn");
              }
              if (!orderLine.returnQuantity || orderLine.returnQuantity < 1) {
                invalidProps.push("returnQuantity");
              }
            }
            orderLine.invalidProps = invalidProps;
          });
          this.orderData = CopyHelpers.deepCopy(this.orderData);
          this.returnItemChangeEvent.emit(this.orderData);
        }
      }
    }
  }

  //just to remove errors from selected return item
  validateSelectedReturnItem() {
    let returnOrder: any;
    this.store
      .pipe(select("returnOrder"))
      .pipe(take(1))
      .subscribe((ro) => {
        returnOrder = ro;
      });
    if ((returnOrder.items && returnOrder.items.length) || true) {
      if (this.orderData.orders.length) {
        const order = this.orderData.orders.find((o) => o.orderNumber === returnOrder.orderNumber);
        if (order) {
          order.orderLines.forEach((orderLine) => {
            const item = returnOrder.items.find((i) => i.internalIdentifier === orderLine.internalIdentifier);
            if (item) {
              if (item.returnableQuantity === 1) {
                orderLine.returnQuantity = 1;
              }
              if (this.status) {
                const invalidProps = [];
                if (!orderLine.reasonForReturn || orderLine.reasonForReturn === "") {
                  invalidProps.push("reasonForReturn");
                }
                if (!orderLine.returnQuantity || orderLine.returnQuantity < 1) {
                  invalidProps.push("returnQuantity");
                }
                orderLine.invalidProps = invalidProps;
              }
            }
          });
          this.orderData = CopyHelpers.deepCopy(this.orderData);
          this.returnItemChangeEvent.emit(this.orderData);
        }
      }
    }
  }

  ngOnDestroy() {
    if (this.translationSubscription) {
      this.translationSubscription.unsubscribe();
    }
  }

  isItemNonReturnable(item: any): boolean {
    return item?.tagBasedReturnable === false;
  }

  getShopifyData() {
    this.extractedShopifyItems = [];
    const orders = this.externalIntegrationsService.shopifyOrderDetails?.data.orders.edges;

    orders?.forEach((orderEdge) => {
      const order = orderEdge.node;
      const lineItems = order.lineItems.edges;

      lineItems.forEach((lineItemEdge) => {
        const lineItem = lineItemEdge.node;
        const variant = lineItem.variant;
        const mediaEdge = variant?.media?.edges?.[0];
        const imageUrl = mediaEdge?.node?.image?.url;
        const altText = mediaEdge?.node?.image?.altText || "";
        const unitPrice = lineItem.originalUnitPriceSet.shopMoney.amount;
        const currency = lineItem.originalUnitPriceSet.shopMoney.currencyCode;
        const description = lineItem.variant.title;

        this.extractedShopifyItems.push({
          imageUrl: imageUrl,
          itemName: lineItem.name,
          itemNumber: lineItem.sku,
          quantity: lineItem.quantity,
          orderNumber: order.name,
          unitPrice: unitPrice,
          currency: currency,
          altText: altText,
          description: description,
          brand: "",
        });
      });
    });
  }
}
